import MainMarker from 'Images/generic_marker.png';

const { LatLng, Marker, Point, Size } = google.maps;

const DEFAULT_LAT = 32;
const DEFAULT_LNG = -97;
const DEFAULT_COORDS = Object.freeze({
  latitude: DEFAULT_LAT,
  longitude: DEFAULT_LNG
});
const ICON_WIDTH = 25;
const ANCHOR_LAT = ICON_WIDTH / 2;
const ICON_HEIGHT = 33;
const MAP_OPTIONS = {
  zoom: 5,
  minZoom: 3,
};

/**
 * @typedef {object} Coordinate
 * @property {string|number} [longitude]
 * @property {string|number} [lng]
 * @property {string|number} [latitude]
 * @property {string|number} [lat]
 */

class SiteMap {

  /**
   * Create a an instance of SiteMap
   *
   * @param {object} MapService - MapService that injected into site component
   * @param {Function} setPosition - A setter function to update the lat and long of the parent's site marker
   * @param {boolean} drawingManagerOn - Optional parameter to add a drawingManager to the map
   */
  constructor(MapService, setPosition, drawingManagerOn = false) {
    const mapImage = {
      url: MainMarker,
      size: new Size(ICON_WIDTH, ICON_HEIGHT),
      origin: new Point(0, 0),
      anchor: new Point(ANCHOR_LAT, ICON_HEIGHT)
    };
    let map;
    let mapMarker = null;
    let shape = null;
    let drawingManager;
    let controlUI;
    let deleteButton;
    let zoomListener;

    this.unlisten = () => {
      if (map) {
        google.maps.event.clearListeners(map, 'click');
      }
      if (zoomListener) {
        google.maps.event.removeListener(zoomListener);
      }
      // May need to clean up the shapePath listeners as well..
      if (mapMarker) {
        mapMarker.setMap(null); // remove marker from the map
        mapMarker = null; // destroy the marker
      }
      if (drawingManager) {
        google.maps.event.clearListeners(drawingManager);
        drawingManager.setMap(null);
        drawingManager = null;
      }
      if (shape) {
        shape.setMap(null);
        shape = null;
      }
      if (controlUI) {
        google.maps.event.clearListeners(controlUI, 'click');
        deleteButton.remove(); // destroy the custom delete button
      }
    };

    this.drawMap = (coordinates, equipment, mapElement, mapCacheId, mapOptions = {}) => {
      const coords = _.merge({}, DEFAULT_COORDS, coordinates);
      map = new MapService.Map(mapElement, mapCacheId, { ...MAP_OPTIONS, ...mapOptions });
      map.setCenter(new LatLng(coords.latitude, coords.longitude));
      this.unlisten();

      if (coordinates.border && coordinates.latitude && coordinates.longitude) {
        for (const eq of _.filter(equipment, MapService.eqIsMappable)) {
          if (eq.settings && eq.settings.latitude && eq.settings.longitude) {
            mapMarker = new Marker({
              map,
              position: new LatLng(eq.settings.latitude, eq.settings.longitude),
              icon: MainMarker,
              visible: true,
              title: eq.name
            });
          }
        }
        const editable = drawingManagerOn;
        shape = new MapService.Polygon(map, coordinates.border, editable);
        createPolygonListeners(shape.getPath());
        const zoomAroundPolygon = MapService.getBoundsZoomLevel(shape.getBounds());
        map.setZoom(zoomAroundPolygon);
        createMapMarker(coordinates.latitude, coordinates.longitude, zoomAroundPolygon);
      }

      if (drawingManagerOn) {
        deleteButton = document.createElement('div');
        createCustomControl(deleteButton);
        deleteButton.index = 1;
        map.controls[google.maps.ControlPosition.TOP_CENTER].push(deleteButton);
        if (coordinates.border) {
          drawingManager = new MapService.DrawingManager(map, {
            drawingControl: false,
            drawingMode: null
          });
          controlUI.style.visibility = 'visible';
        } else {
          drawingManager = new MapService.DrawingManager(map);
        }
        google.maps.event.addListener(drawingManager, 'overlaycomplete', (event) => {
          shape = event.overlay; // storing this so we can call setMap(null) on it later
          const shapePath = shape.getPath(); // array of ordered points around polygon
          const centroid = calculateCentroid(shapePath);
          controlUI.style.visibility = 'visible';
          setPosition(centroid.lat, centroid.lng, shapePath.getArray());
          drawingManager.setOptions({
            drawingControl: false, // Hides the drawing manager
            drawingMode: null // Switch back to non-drawing mode after drawing a shapePath.
          });
          createPolygonListeners(shapePath);
          const zoomAroundPolygon = MapService.getBoundsZoomLevel(shape.getBounds());
          createMapMarker(centroid.lat, centroid.lng, zoomAroundPolygon);
        });
      }
    };


    /**
     * Given n vertices of a polygon, calculates the centroid
     * This is the sum of vertices position / number of vertices
     *
     * @param {Array<object>} polygonPath Polygon coordinates
     * @returns {Coordinate} Centroid
     */
    function calculateCentroid(polygonPath) {
      let latSum = 0;
      let lngSum = 0;
      polygonPath.forEach((path) => {
        latSum += path.lat();
        lngSum += path.lng();
      });
      const centroid = {
        lat: latSum / polygonPath.length,
        lng: lngSum / polygonPath.length
      };
      setMarkerCoordinates(centroid.lat, centroid.lng);
      return centroid;
    }

    /**
     * Draws the marker on the map and starts a listener to watch zoom level
     *
     * @param {number} latitude - latitude coordinate to draw marker
     * @param {number} longitude - longitude coordinate to draw marker
     * @param {number} zoomAroundPolygon - google maps zoom level that is tight around polygon (0-21)
     * @returns {undefined}
     */
    function createMapMarker(latitude, longitude, zoomAroundPolygon) {
      mapMarker = new Marker({
        map,
        position: new LatLng(latitude, longitude),
        icon: mapImage,
        visible: false
      });
      map.setCenter(mapMarker.getPosition()); // Needs a center or zoom will bug out
      zoomListener = google.maps.event.addListener(map, 'zoom_changed', () => {
        const isVisible = map.getZoom() <= zoomAroundPolygon - 4;
        mapMarker && mapMarker.setVisible(isVisible);
        shape && shape.setVisible(!isVisible);
      });
    }

    /**
     * Updates the marker position on the map to the center of the polygon
     *
     * @param {string[]} coordinates - latitude and longitude coordinates [lat, long]
     * @returns {undefined}
     */
    function setMarkerCoordinates(...coordinates) {
      const position = new LatLng(...coordinates);
      if (mapMarker) {
        mapMarker.setPosition(position);
      } else {
        mapMarker = new Marker({
          map,
          position,
          icon: mapImage,
          visible: false
        });
      }
    }

    /**
     * Updates the marker position on the map to the center of the polygon
     *
     * @param {object} polygonPath Polygon
     * @returns {undefined}
     */
    function createPolygonListeners(polygonPath) {
      google.maps.event.addListener(polygonPath, 'insert_at', () => {
        // insert_at fired when we modify a polygon area from a corner dot
        const centroid = calculateCentroid(polygonPath);
        setPosition(centroid.lat, centroid.lng, polygonPath.getArray());
      });
      google.maps.event.addListener(polygonPath, 'set_at', () => {
        // set_at is fired when we drag a side point to create a new vertex
        const centroid = calculateCentroid(polygonPath);
        setPosition(centroid.lat, centroid.lng, polygonPath.getArray());
      });
    }

    /**
     * Creates a custom button that resembles drawing manager buttons
     *
     * @param {string} controlDiv - The div where the button going
     * @returns {undefined}
     */
    function createCustomControl(controlDiv) {

      // Set CSS for the control border
      controlUI = document.createElement('div');
      controlUI.style.cursor = 'pointer';
      controlUI.style.textAlign = 'center';
      controlUI.style.visibility = 'hidden';
      controlUI.title = 'Click to remove polygon';
      controlDiv.appendChild(controlUI);

      // Set CSS for the control interior
      const controlText = document.createElement('div');
      controlText.style.fontFamily = 'Arial,sans-serif';
      controlText.style.fontSize = '16px';
      controlText.style.borderStyle = 'solid';
      controlText.style.borderWidth = '1px';
      controlText.style.borderColor = '#ccc';
      controlText.style.borderRadius = '4px';
      controlText.style.paddingLeft = '4px';
      controlText.style.paddingRight = '4px';
      controlText.style.paddingTop = '7px';
      controlText.style.paddingBottom = '7px';
      controlText.style.backgroundColor = 'white';
      controlText.innerHTML = '<i class="far fa-trash-alt" aria-hidden="true"></i>';
      controlUI.appendChild(controlText);

      // Center and restyle the button to match Drawing Manager
      controlDiv.style.marginRight = '-80px';
      controlDiv.style.marginTop = '10px';
      // Setup the listener for delete button
      google.maps.event.addDomListener(controlUI, 'click', () => {
        controlUI.style.visibility = 'hidden'; // hide the delete button
        if (shape) {
          shape.setMap(null); // remove the shape and marker
          shape = null;
        }
        if (mapMarker) {
          mapMarker.setMap(null);
          mapMarker = null;
        }
        setPosition(null, null, null); // makes sure the shapePath's values don't persist
        drawingManager.setOptions({
          drawingControl: true,
          drawingMode: 'polygon'
        });
      });
    }
  }
}


export default SiteMap;
